const TIME_DEFAULTS = {
  hour: "12",
  minute: "00",
  second: "00",
  ampm: "PM",
};

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export function sanitizeDigits(value, maxLength) {
  return String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, maxLength);
}

export function sanitizeAmpmInput(value) {
  return String(value ?? "")
    .replace(/[^a-z]/gi, "")
    .toUpperCase()
    .slice(0, 2);
}

export function normalizeAmpmValue(value, fallback = TIME_DEFAULTS.ampm) {
  const normalized = sanitizeAmpmInput(value);
  if (normalized.startsWith("P")) return "PM";
  if (normalized.startsWith("A")) return "AM";
  return fallback === "AM" ? "AM" : "PM";
}

export function getDaysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function normalizeNumericField(value, { min, max, fallback, padLength = 0 }) {
  const digits = sanitizeDigits(value, 4);
  const parsed = digits === "" ? Number.NaN : Number.parseInt(digits, 10);
  const safeFallback = clamp(
    Number.isFinite(fallback) ? fallback : min,
    min,
    max,
  );
  const normalized = Number.isFinite(parsed)
    ? clamp(parsed, min, max)
    : safeFallback;

  return padLength > 0
    ? String(normalized).padStart(padLength, "0")
    : String(normalized);
}

export function isCompleteTimeValue(value) {
  const hour = Number.parseInt(value?.hour, 10);
  const minute = Number.parseInt(value?.minute, 10);
  const second = Number.parseInt(value?.second, 10);
  return (
    Number.isInteger(hour) &&
    hour >= 1 &&
    hour <= 12 &&
    Number.isInteger(minute) &&
    minute >= 0 &&
    minute <= 59 &&
    Number.isInteger(second) &&
    second >= 0 &&
    second <= 59 &&
    (value?.ampm === "AM" || value?.ampm === "PM")
  );
}

export function normalizeTimeValue(value, fallback = TIME_DEFAULTS) {
  const base = isCompleteTimeValue(fallback)
    ? {
        hour: String(Number.parseInt(fallback.hour, 10)),
        minute: String(Number.parseInt(fallback.minute, 10)).padStart(2, "0"),
        second: String(Number.parseInt(fallback.second, 10)).padStart(2, "0"),
        ampm: normalizeAmpmValue(fallback.ampm),
      }
    : TIME_DEFAULTS;

  return {
    hour: normalizeNumericField(value?.hour, {
      min: 1,
      max: 12,
      fallback: Number.parseInt(base.hour, 10),
    }),
    minute: normalizeNumericField(value?.minute, {
      min: 0,
      max: 59,
      fallback: Number.parseInt(base.minute, 10),
      padLength: 2,
    }),
    second: normalizeNumericField(value?.second, {
      min: 0,
      max: 59,
      fallback: Number.parseInt(base.second, 10),
      padLength: 2,
    }),
    ampm: normalizeAmpmValue(value?.ampm, base.ampm),
  };
}

export function toggleAmpm(value) {
  return value === "AM" ? "PM" : "AM";
}

export function incrementTimeField(value, field, fallback = TIME_DEFAULTS) {
  const parts = normalizeTimeValue(value, fallback);

  if (field === "ampm") {
    return { ...parts, ampm: toggleAmpm(parts.ampm) };
  }

  if (field === "hour") {
    const hour = Number.parseInt(parts.hour, 10);
    if (hour === 11) {
      return { ...parts, hour: "12", ampm: toggleAmpm(parts.ampm) };
    }
    if (hour === 12) {
      return { ...parts, hour: "1" };
    }
    return { ...parts, hour: String(hour + 1) };
  }

  if (field === "minute") {
    const minute = Number.parseInt(parts.minute, 10);
    if (minute === 59) {
      const nextHour = incrementTimeField(parts, "hour", fallback);
      return { ...nextHour, minute: "00" };
    }
    return { ...parts, minute: String(minute + 1).padStart(2, "0") };
  }

  if (field === "second") {
    const second = Number.parseInt(parts.second, 10);
    if (second === 59) {
      const nextMinute = incrementTimeField(parts, "minute", fallback);
      return { ...nextMinute, second: "00" };
    }
    return { ...parts, second: String(second + 1).padStart(2, "0") };
  }

  return parts;
}

export function decrementTimeField(value, field, fallback = TIME_DEFAULTS) {
  const parts = normalizeTimeValue(value, fallback);

  if (field === "ampm") {
    return { ...parts, ampm: toggleAmpm(parts.ampm) };
  }

  if (field === "hour") {
    const hour = Number.parseInt(parts.hour, 10);
    if (hour === 12) {
      return { ...parts, hour: "11", ampm: toggleAmpm(parts.ampm) };
    }
    if (hour === 1) {
      return { ...parts, hour: "12" };
    }
    return { ...parts, hour: String(hour - 1) };
  }

  if (field === "minute") {
    const minute = Number.parseInt(parts.minute, 10);
    if (minute === 0) {
      const previousHour = decrementTimeField(parts, "hour", fallback);
      return { ...previousHour, minute: "59" };
    }
    return { ...parts, minute: String(minute - 1).padStart(2, "0") };
  }

  if (field === "second") {
    const second = Number.parseInt(parts.second, 10);
    if (second === 0) {
      const previousMinute = decrementTimeField(parts, "minute", fallback);
      return { ...previousMinute, second: "59" };
    }
    return { ...parts, second: String(second - 1).padStart(2, "0") };
  }

  return parts;
}

export function getDefaultDateTimeParts(date = new Date()) {
  const hours24 = date.getHours();
  return {
    year: String(date.getFullYear()),
    month: String(date.getMonth() + 1),
    day: String(date.getDate()),
    hour: String(hours24 % 12 || 12),
    minute: String(date.getMinutes()).padStart(2, "0"),
    second: String(date.getSeconds()).padStart(2, "0"),
    ampm: hours24 >= 12 ? "PM" : "AM",
  };
}

export function isDatePartiallyEmpty(value) {
  return [value?.year, value?.month, value?.day, value?.hour, value?.minute, value?.second]
    .every((part) => String(part ?? "").trim() === "") &&
    String(value?.ampm ?? "").trim() === "";
}

export function isCompleteDateTimeValue(value) {
  const year = Number.parseInt(value?.year, 10);
  const month = Number.parseInt(value?.month, 10);
  const day = Number.parseInt(value?.day, 10);

  if (
    !Number.isInteger(year) ||
    year < 1 ||
    year > 9999 ||
    !Number.isInteger(month) ||
    month < 1 ||
    month > 12
  ) {
    return false;
  }

  const maxDay = getDaysInMonth(year, month);
  if (!Number.isInteger(day) || day < 1 || day > maxDay) {
    return false;
  }

  return isCompleteTimeValue(value);
}

export function normalizeDateTimeValue(
  value,
  fallback = getDefaultDateTimeParts(),
) {
  const base = isCompleteDateTimeValue(fallback)
    ? fallback
    : getDefaultDateTimeParts();

  const year = normalizeNumericField(value?.year, {
    min: 1,
    max: 9999,
    fallback: Number.parseInt(base.year, 10),
  });
  const month = normalizeNumericField(value?.month, {
    min: 1,
    max: 12,
    fallback: Number.parseInt(base.month, 10),
  });
  const maxDay = getDaysInMonth(
    Number.parseInt(year, 10),
    Number.parseInt(month, 10),
  );
  const day = normalizeNumericField(value?.day, {
    min: 1,
    max: maxDay,
    fallback: clamp(Number.parseInt(base.day, 10), 1, maxDay),
  });
  const time = normalizeTimeValue(value, base);

  return {
    year,
    month,
    day,
    ...time,
  };
}

export function areTimeValuesEqual(left, right) {
  return (
    left?.hour === right?.hour &&
    left?.minute === right?.minute &&
    left?.second === right?.second &&
    left?.ampm === right?.ampm
  );
}

export function areDateTimeValuesEqual(left, right) {
  return (
    left?.year === right?.year &&
    left?.month === right?.month &&
    left?.day === right?.day &&
    areTimeValuesEqual(left, right)
  );
}

export function toDateTimePartsFromIsoString(value) {
  if (typeof value !== "string") return null;

  const match = value
    .trim()
    .replace(" ", "T")
    .match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/,
    );

  if (!match) return null;

  const [, yearString, monthString, dayString, hourString, minuteString, secondString = "00"] =
    match;
  const year = Number.parseInt(yearString, 10);
  const month = Number.parseInt(monthString, 10);
  const day = Number.parseInt(dayString, 10);
  const hour24 = Number.parseInt(hourString, 10);
  const minute = Number.parseInt(minuteString, 10);
  const second = Number.parseInt(secondString, 10);

  if (
    !Number.isInteger(year) ||
    year < 1 ||
    year > 9999 ||
    !Number.isInteger(month) ||
    month < 1 ||
    month > 12 ||
    !Number.isInteger(day) ||
    day < 1 ||
    day > getDaysInMonth(year, month) ||
    !Number.isInteger(hour24) ||
    hour24 < 0 ||
    hour24 > 23 ||
    !Number.isInteger(minute) ||
    minute < 0 ||
    minute > 59 ||
    !Number.isInteger(second) ||
    second < 0 ||
    second > 59
  ) {
    return null;
  }

  return {
    year: String(year),
    month: String(month),
    day: String(day),
    hour: String(hour24 % 12 || 12),
    minute: String(minute).padStart(2, "0"),
    second: String(second).padStart(2, "0"),
    ampm: hour24 >= 12 ? "PM" : "AM",
  };
}

export function serializeDateTimeValue(value) {
  const parts = normalizeDateTimeValue(value);
  const hour12 = Number.parseInt(parts.hour, 10);
  let hour24 = hour12;

  if (parts.ampm === "AM") {
    if (hour12 === 12) hour24 = 0;
  } else if (hour12 !== 12) {
    hour24 = hour12 + 12;
  }

  return [
    String(Number.parseInt(parts.year, 10)).padStart(4, "0"),
    "-",
    String(Number.parseInt(parts.month, 10)).padStart(2, "0"),
    "-",
    String(Number.parseInt(parts.day, 10)).padStart(2, "0"),
    "T",
    String(hour24).padStart(2, "0"),
    ":",
    String(Number.parseInt(parts.minute, 10)).padStart(2, "0"),
    ":",
    String(Number.parseInt(parts.second, 10)).padStart(2, "0"),
  ].join("");
}

function toDateFromParts(value, fallback) {
  const parts = normalizeDateTimeValue(value, fallback);
  const hour12 = Number.parseInt(parts.hour, 10);
  let hour24 = hour12;

  if (parts.ampm === "AM") {
    if (hour12 === 12) hour24 = 0;
  } else if (hour12 !== 12) {
    hour24 = hour12 + 12;
  }

  return new Date(
    Number.parseInt(parts.year, 10),
    Number.parseInt(parts.month, 10) - 1,
    Number.parseInt(parts.day, 10),
    hour24,
    Number.parseInt(parts.minute, 10),
    Number.parseInt(parts.second, 10),
  );
}

function fromDateToParts(date) {
  return getDefaultDateTimeParts(date);
}

export function incrementDateTimeField(
  value,
  field,
  fallback = getDefaultDateTimeParts(),
) {
  const parts = normalizeDateTimeValue(value, fallback);

  if (field === "year") {
    const year = clamp(Number.parseInt(parts.year, 10) + 1, 1, 9999);
    const day = clamp(
      Number.parseInt(parts.day, 10),
      1,
      getDaysInMonth(year, Number.parseInt(parts.month, 10)),
    );
    return { ...parts, year: String(year), day: String(day) };
  }

  if (field === "month") {
    const month = Number.parseInt(parts.month, 10);
    const year = Number.parseInt(parts.year, 10);
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? clamp(year + 1, 1, 9999) : year;
    const day = clamp(
      Number.parseInt(parts.day, 10),
      1,
      getDaysInMonth(nextYear, nextMonth),
    );
    return {
      ...parts,
      year: String(nextYear),
      month: String(nextMonth),
      day: String(day),
    };
  }

  if (field === "day") {
    const date = toDateFromParts(parts, fallback);
    date.setDate(date.getDate() + 1);
    return fromDateToParts(date);
  }

  if (field === "hour") {
    const date = toDateFromParts(parts, fallback);
    date.setHours(date.getHours() + 1);
    return fromDateToParts(date);
  }

  if (field === "minute") {
    const date = toDateFromParts(parts, fallback);
    date.setMinutes(date.getMinutes() + 1);
    return fromDateToParts(date);
  }

  if (field === "second") {
    const date = toDateFromParts(parts, fallback);
    date.setSeconds(date.getSeconds() + 1);
    return fromDateToParts(date);
  }

  if (field === "ampm") {
    const date = toDateFromParts(parts, fallback);
    date.setHours(date.getHours() + 12);
    return fromDateToParts(date);
  }

  return parts;
}

export function decrementDateTimeField(
  value,
  field,
  fallback = getDefaultDateTimeParts(),
) {
  const parts = normalizeDateTimeValue(value, fallback);

  if (field === "year") {
    const year = clamp(Number.parseInt(parts.year, 10) - 1, 1, 9999);
    const day = clamp(
      Number.parseInt(parts.day, 10),
      1,
      getDaysInMonth(year, Number.parseInt(parts.month, 10)),
    );
    return { ...parts, year: String(year), day: String(day) };
  }

  if (field === "month") {
    const month = Number.parseInt(parts.month, 10);
    const year = Number.parseInt(parts.year, 10);
    const nextMonth = month === 1 ? 12 : month - 1;
    const nextYear = month === 1 ? clamp(year - 1, 1, 9999) : year;
    const day = clamp(
      Number.parseInt(parts.day, 10),
      1,
      getDaysInMonth(nextYear, nextMonth),
    );
    return {
      ...parts,
      year: String(nextYear),
      month: String(nextMonth),
      day: String(day),
    };
  }

  if (field === "day") {
    const date = toDateFromParts(parts, fallback);
    date.setDate(date.getDate() - 1);
    return fromDateToParts(date);
  }

  if (field === "hour") {
    const date = toDateFromParts(parts, fallback);
    date.setHours(date.getHours() - 1);
    return fromDateToParts(date);
  }

  if (field === "minute") {
    const date = toDateFromParts(parts, fallback);
    date.setMinutes(date.getMinutes() - 1);
    return fromDateToParts(date);
  }

  if (field === "second") {
    const date = toDateFromParts(parts, fallback);
    date.setSeconds(date.getSeconds() - 1);
    return fromDateToParts(date);
  }

  if (field === "ampm") {
    const date = toDateFromParts(parts, fallback);
    date.setHours(date.getHours() - 12);
    return fromDateToParts(date);
  }

  return parts;
}
